// Numbers in the user's language and the figures of a shot (FR-17: times of shots in the browser's time zone).
import type { ShotItem } from '@brewcore/app-contract';
import { t, typeset, type Lang } from '../i18n.js';
import { localDayOf } from '../schedule.js';

/** Decimals a catalog step shows: 1 -> 0, 0.5/0.1 -> 1, 0.01 -> 2. */
export const decimalsOf = (step: number | undefined): number =>
  !step || step >= 1 ? 0 : step >= 0.1 ? 1 : 2;

export function formatNumber(v: number, decimals: number, lang: Lang): string {
  const x = Math.abs(v) < 0.5 / 10 ** decimals ? 0 : v;
  return x.toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Whole numbers without, halves with one decimal ("18 g", "18,5 g"). */
export const formatAuto = (v: number, lang: Lang): string => formatNumber(v, v % 1 ? 1 : 0, lang);

/**
 * Parts of a summary line ("Tag 9 · 18 g → 36 g · Mahlgrad 2,4"): a line may break after a dot, never before it, so
 * no line starts with "·" (review 19.10.2026: "Mahlgrad 2,5 / · 93,0 °C"), and a number keeps its unit (typeset).
 */
export const SEP = '\u00a0· ';
export const dotJoin = (parts: readonly (string | null | undefined | false)[]): string =>
  typeset(parts.filter(Boolean).join(SEP));

/** "+1,2", "−0,4", "±0,0" */
export const signed = (v: number, lang: Lang): string =>
  `${v > 0 ? '+' : v < 0 ? '−' : '±'}${formatNumber(Math.abs(v), 1, lang)}`;

/** The meta of a shot (protocol v1 shot.meta) as far as the screens use it. */
export interface ShotMeta {
  duration?: number;
  weight?: number | null;
  dose?: number | null;
  grind?: string;
  bean?: { id?: number; name: string } | null;
  stopMode?: 'hand' | 'time' | 'weight';
  targetTime?: number | null;
  targetWeight?: number | null;
  /** setpoint and profile (K towards the end) the shot started with; absent before the firmware of 19.10.2026 */
  setpoint?: number | null;
  tempEnd?: number | null;
  startTemp?: number | null;
  firstDrop?: number | null;
  stopWeight?: number | null;
  lead?: number | null;
  lag?: number | null;
  flowAtStop?: number | null;
  preinfusion?: { burst: number; pause: number; valveOpen?: boolean } | null;
  channeling?: boolean;
  stoppedByHand?: boolean;
  cold?: boolean;
}

export const metaOf = (s: ShotItem): ShotMeta => s.meta as ShotMeta;

/** "nach Gewicht", "nach Zeit", "von Hand"; '' when the shot does not say (older shots by time or by hand). */
export function stopModeText(m: ShotMeta, lang: Lang): string {
  if (m.stopMode === 'weight') return t(lang, 'stopByWeight');
  if (m.stopMode === 'time') return t(lang, 'stopByTime');
  return m.stopMode === 'hand' ? t(lang, 'stopHand') : '';
}

export const piSecondsOf = (m: ShotMeta): number =>
  m.preinfusion ? m.preinfusion.burst + m.preinfusion.pause : 0;

/** Brew time without the pre-infusion (the display counts it apart, "+6 s"). */
export function shotTime(m: ShotMeta): { main: number; pi: number } {
  const pi = piSecondsOf(m);
  return { main: Math.max(0, (m.duration ?? 0) - pi), pi };
}

/** Deviation from the target (g by weight, s by time without PI), null without a target. */
export function deviation(m: ShotMeta): { value: number; unit: 'g' | 's' } | null {
  if (m.stopMode === 'weight' && m.targetWeight && typeof m.weight === 'number')
    return { value: Math.round((m.weight - m.targetWeight) * 10) / 10, unit: 'g' };
  if (m.stopMode === 'time' && m.targetTime && typeof m.duration === 'number')
    return { value: Math.round((m.duration - piSecondsOf(m) - m.targetTime) * 10) / 10, unit: 's' };
  return null;
}

/** A deviation of more than 1 g or 1 s is shown as off target. */
export const offTarget = (d: { value: number } | null): boolean => d !== null && Math.abs(d.value) > 1;

/** When the shot happened: the machine's time, else the time it arrived. */
export const shotMs = (s: ShotItem): number => (s.occurredAt !== null ? s.occurredAt * 1000 : s.receivedAt);

/** "heute 08:12", "gestern 19:03", "16.10. 07:45" in the browser's zone (zone only for tests). */
export function whenText(ms: number, lang: Lang, now: number, zone?: string): string {
  const loc = lang === 'de' ? 'de-DE' : 'en-GB';
  const d = new Date(ms);
  const hm = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', timeZone: zone });
  const days = localDayOf(now, zone) - localDayOf(ms, zone);
  if (days === 0) return `${t(lang, 'today')} ${hm}`;
  if (days === 1) return `${t(lang, 'yesterday')} ${hm}`;
  return `${d.toLocaleDateString(loc, { day: '2-digit', month: '2-digit', timeZone: zone })} ${hm}`;
}

/** The day only: "heute", "gestern", "07.10." (a list of beans: when one was last used). */
export function lastDayText(ms: number, lang: Lang, now: number, zone?: string): string {
  const days = localDayOf(now, zone) - localDayOf(ms, zone);
  if (days === 0) return t(lang, 'today');
  if (days === 1) return t(lang, 'yesterday');
  return new Date(ms).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    timeZone: zone,
  });
}

/** Unix seconds -> "18.10.2026" / "18/10/2026"; "–" without a date. */
export function dateText(atSec: number | null | undefined, lang: Lang, zone?: string): string {
  if (!atSec) return '–';
  return new Date(atSec * 1000).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: zone,
  });
}

/** Coffee used: "850 g", "12,4 kg". */
export const coffeeText = (grams: number, lang: Lang): string =>
  grams >= 1000 ? `${formatNumber(grams / 1000, 1, lang)} kg` : `${formatNumber(grams, 0, lang)} g`;
